import { useEffect, useState } from 'react';
import { attachmentApi } from '@core/api';
import { cn } from '@core/ui';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const pending = new Map<string, Promise<string | undefined>>();

export function attachmentIdFromMediaSource(source: string): string | undefined {
  if (source.startsWith('attachment:')) return source.slice('attachment:'.length);
  // Older team branding stored short lived S3 URLs. Their final path segment
  // is the attachment ID; the attachment API can issue a fresh URL for it.
  try {
    const url = new URL(source);
    if (!url.searchParams.has('X-Amz-Algorithm') && !url.searchParams.has('AWSAccessKeyId')) return;
    const id = decodeURIComponent(url.pathname).split('/').filter(Boolean).slice(-1)[0] ?? '';
    return UUID.test(id) ? id : undefined;
  } catch {
    return;
  }
}

export function useMediaUrl(source?: string | null): string | undefined {
  const [resolved, setResolved] = useState<{ source: string; url?: string }>();
  const id = source ? attachmentIdFromMediaSource(source) : undefined;
  useEffect(() => {
    if (!id || !source) return;
    let active = true;
    let request = pending.get(id);
    if (!request) {
      request = attachmentApi.GetDownloadUrl(id)
        .then((result) => result.download_url?.url)
        .catch(() => undefined)
        .finally(() => pending.delete(id));
      pending.set(id, request);
    }
    request.then((url) => { if (active) setResolved({ source, url }); });
    return () => { active = false; };
  }, [id, source]);
  if (!source) return;
  if (!id) return source;
  return resolved?.source === source ? resolved.url : undefined;
}

export function MediaImage({ source, alt = '', className, fallbackClassName }: {
  source?: string | null;
  alt?: string;
  className?: string;
  fallbackClassName?: string;
}) {
  const url = useMediaUrl(source);
  const [failed, setFailed] = useState<string>();
  if (!url || url === failed) return <div aria-hidden="true" className={cn('bg-muted', fallbackClassName)} />;
  return <img src={url} alt={alt} className={className} onError={() => setFailed(url)} />;
}
